import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import {
  ActivationJourneyService,
  activationStepToSlug,
} from '../onboarding-checklist/activation-journey.service';
import { AANYA_EXAMPLE_WORKDAY } from './example-workday-data';
import {
  ExampleDataSource,
  ExampleWorkday,
  ExampleWorkdayStepId,
} from './example-workday.model';

const SESSION_ACTIVE_KEY = 'supersee_example_workday_active';
const SESSION_STEP_KEY = 'supersee_example_workday_step';
const SESSION_OPENED_AT_KEY = 'supersee_example_workday_opened_at';
const SESSION_RETURN_KEY = 'supersee_example_workday_return';
const SESSION_ENTRY_KEY = 'supersee_example_workday_entry';
const SESSION_RETURN_QUERY_KEY = 'supersee_example_workday_return_query';

const EXAMPLE_ENTRY_CONTEXTS: ExampleEntryContext[] = [
  'ready',
  'understand',
  'proof',
  'explore',
  'apps',
  'hours',
  'resume',
  'help',
];

const EXAMPLE_STEP_IDS: ExampleWorkdayStepId[] = [
  'replay',
  'usage',
  'hours',
  'trends',
  'insights',
];

/** Closed set of Example entry contexts (plan contract). */
export type ExampleEntryContext =
  | 'ready'
  | 'understand'
  | 'proof'
  | 'explore'
  | 'apps'
  | 'hours'
  | 'resume'
  | 'help';

/**
 * Scoped Example workday — route is the source of truth.
 * Never powers normal admin pages via DummyDataService substitution.
 */
@Injectable({ providedIn: 'root' })
export class ExampleWorkdayService {
  readonly workday: ExampleWorkday = AANYA_EXAMPLE_WORKDAY;
  readonly dataSource: ExampleDataSource = 'scoped_example';

  constructor(
    private router: Router,
    private activationJourney: ActivationJourneyService
  ) {}

  parseEntryContext(raw: string | null | undefined): ExampleEntryContext | null {
    return EXAMPLE_ENTRY_CONTEXTS.includes(raw as ExampleEntryContext)
      ? (raw as ExampleEntryContext)
      : null;
  }

  parseStepId(raw: string | null | undefined): ExampleWorkdayStepId | null {
    return EXAMPLE_STEP_IDS.includes(raw as ExampleWorkdayStepId)
      ? (raw as ExampleWorkdayStepId)
      : null;
  }

  getEntryContext(): ExampleEntryContext | null {
    return this.parseEntryContext(sessionStorage.getItem(SESSION_ENTRY_KEY));
  }

  /**
   * Guard helper: active session or a known entry query that can rehydrate Forward.
   */
  canAccessExampleRoute(entryQuery?: string | null): boolean {
    if (this.isSessionActive() && this.getEntryContext()) {
      return true;
    }
    return !!this.parseEntryContext(entryQuery);
  }

  /**
   * Restore session from ?entry= after Back cleared sessionStorage (Forward revive).
   * Returns false when neither session nor query can authorize the Example route.
   */
  ensureSessionFromRoute(
    entryQuery: string | null | undefined,
    stepQuery?: string | null | undefined
  ): boolean {
    let entry = this.getEntryContext();
    if (!entry) {
      entry = this.parseEntryContext(entryQuery);
      if (!entry) {
        return false;
      }
      sessionStorage.setItem(SESSION_ENTRY_KEY, entry);
      sessionStorage.setItem(SESSION_RETURN_KEY, this.defaultReturnForEntry(entry));
      sessionStorage.setItem(SESSION_ACTIVE_KEY, '1');
      if (!sessionStorage.getItem(SESSION_OPENED_AT_KEY)) {
        sessionStorage.setItem(SESSION_OPENED_AT_KEY, String(Date.now()));
      }
      const stepId = this.parseStepId(stepQuery) || 'replay';
      sessionStorage.setItem(SESSION_STEP_KEY, stepId);
      this.activationJourney.trackEvent('example_workday_opened', {
        data_source: this.dataSource,
        step: stepId,
        entry,
        surface: 'guide_v2',
        rehydrated: true,
      });
      return true;
    }
    if (!this.isSessionActive()) {
      sessionStorage.setItem(SESSION_ACTIVE_KEY, '1');
      if (!sessionStorage.getItem(SESSION_OPENED_AT_KEY)) {
        sessionStorage.setItem(SESSION_OPENED_AT_KEY, String(Date.now()));
      }
    }
    return true;
  }

  getStoredStepId(): ExampleWorkdayStepId | null {
    return this.parseStepId(sessionStorage.getItem(SESSION_STEP_KEY));
  }

  getStepIndex(stepId: ExampleWorkdayStepId): number {
    return Math.max(
      0,
      this.workday.steps.findIndex((s) => s.id === stepId)
    );
  }

  getStep(stepId: ExampleWorkdayStepId) {
    return this.workday.steps.find((s) => s.id === stepId) || this.workday.steps[0];
  }

  markOpened(stepId: ExampleWorkdayStepId = 'replay'): void {
    sessionStorage.setItem(SESSION_ACTIVE_KEY, '1');
    sessionStorage.setItem(SESSION_STEP_KEY, stepId);
    if (!sessionStorage.getItem(SESSION_OPENED_AT_KEY)) {
      sessionStorage.setItem(SESSION_OPENED_AT_KEY, String(Date.now()));
    }
    this.activationJourney.trackEvent('example_workday_opened', {
      data_source: this.dataSource,
      step: stepId,
      entry: this.getEntryContext(),
      surface: 'guide_v2',
    });
  }

  markStepViewed(stepId: ExampleWorkdayStepId): void {
    sessionStorage.setItem(SESSION_STEP_KEY, stepId);
    this.activationJourney.trackEvent('example_workday_step_viewed', {
      data_source: this.dataSource,
      step: stepId,
      entry: this.getEntryContext(),
    });
  }

  markClosed(reason: 'exit' | 'cta' | 'navigate_away'): void {
    const openedAt = Number(sessionStorage.getItem(SESSION_OPENED_AT_KEY) || 0);
    const dwellMs = openedAt ? Date.now() - openedAt : 0;
    const step = sessionStorage.getItem(SESSION_STEP_KEY) || 'replay';
    const entry = this.getEntryContext();
    this.activationJourney.trackEvent('example_workday_closed', {
      data_source: this.dataSource,
      reason,
      step,
      dwell_ms: dwellMs,
      entry,
    });
    sessionStorage.removeItem(SESSION_ACTIVE_KEY);
    sessionStorage.removeItem(SESSION_STEP_KEY);
    sessionStorage.removeItem(SESSION_OPENED_AT_KEY);
    if (reason === 'navigate_away') {
      // Leaving the Example tour into the real admin: pause so setup guard allows browsing.
      if (entry === 'understand' || entry === 'ready') {
        this.activationJourney.continueLater();
      }
      sessionStorage.removeItem(SESSION_RETURN_KEY);
      sessionStorage.removeItem(SESSION_ENTRY_KEY);
      sessionStorage.removeItem(SESSION_RETURN_QUERY_KEY);
    }
  }

  isSessionActive(): boolean {
    return sessionStorage.getItem(SESSION_ACTIVE_KEY) === '1';
  }

  openWithContext(
    entry: ExampleEntryContext,
    opts?: {
      returnRoute?: string;
      returnQuery?: Params;
      step?: ExampleWorkdayStepId;
      replaceUrl?: boolean;
    }
  ): void {
    sessionStorage.setItem(SESSION_ENTRY_KEY, entry);
    if (opts?.returnRoute) {
      sessionStorage.setItem(SESSION_RETURN_KEY, opts.returnRoute);
    } else {
      sessionStorage.setItem(SESSION_RETURN_KEY, this.defaultReturnForEntry(entry));
    }
    if (opts?.returnQuery) {
      sessionStorage.setItem(SESSION_RETURN_QUERY_KEY, JSON.stringify(opts.returnQuery));
    } else {
      sessionStorage.removeItem(SESSION_RETURN_QUERY_KEY);
    }
    const step = opts?.step || 'replay';
    this.markOpened(step);
    // Push one Example history entry; in-page step changes use replaceUrl.
    void this.router.navigate(['/setup/example-workday'], {
      queryParams: { entry, step },
      ...(opts?.replaceUrl ? { replaceUrl: true } : {}),
    });
  }

  /** Open from Ready secondary CTA (legacy). Prefer openFromUnderstand. */
  openFromReady(): void {
    this.openWithContext('ready', { returnRoute: 'ready' });
  }

  /** Open from Understand Look around — return to /setup/understand. */
  openFromUnderstand(): void {
    this.openWithContext('understand', { returnRoute: 'understand', step: 'replay' });
  }

  /** Open from post-proof / explore — remember return target. */
  openGuidedExample(opts?: { fromProof?: boolean; step?: ExampleWorkdayStepId }): void {
    if (opts?.fromProof === false) {
      this.openWithContext('explore', { returnRoute: 'explore', step: opts?.step });
      return;
    }
    this.openWithContext('proof', { returnRoute: 'screenshot', step: opts?.step });
  }

  /** Sync Example step into the URL without stacking history. */
  syncStepQuery(stepId: ExampleWorkdayStepId): void {
    const entry = this.getEntryContext();
    void this.router.navigate(['/setup/example-workday'], {
      queryParams: { entry: entry || undefined, step: stepId },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private defaultReturnForEntry(entry: ExampleEntryContext): string {
    switch (entry) {
      case 'ready':
        return 'ready';
      case 'understand':
        return 'understand';
      case 'proof':
        return 'screenshot';
      case 'explore':
      case 'resume':
        return 'explore';
      case 'apps':
        return 'apps';
      case 'hours':
        return 'hours';
      case 'help':
        return 'ready';
      default:
        return 'ready';
    }
  }

  private readReturnQuery(): Params | undefined {
    try {
      const raw = sessionStorage.getItem(SESSION_RETURN_QUERY_KEY);
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  exitExample(): void {
    const returnTo = sessionStorage.getItem(SESSION_RETURN_KEY);
    const entry = this.getEntryContext();
    const returnQuery = this.readReturnQuery();
    this.markClosed('exit');
    sessionStorage.removeItem(SESSION_RETURN_KEY);
    sessionStorage.removeItem(SESSION_ENTRY_KEY);
    sessionStorage.removeItem(SESSION_RETURN_QUERY_KEY);

    if (returnTo === 'ready' || entry === 'ready') {
      this.activationJourney.goToStep('goal_selected');
      void this.router.navigate(['/setup/ready'], { replaceUrl: true });
      return;
    }

    if (returnTo === 'understand' || entry === 'understand') {
      this.activationJourney.goToStep('plan_viewed');
      void this.router.navigate(['/setup/understand'], { replaceUrl: true });
      return;
    }

    if (returnTo === 'explore') {
      const employeeId = this.activationJourney.getState().privateTestEmployeeId;
      void this.router.navigate(['/screenshots'], {
        queryParams: employeeId ? { employeeId: String(employeeId) } : returnQuery,
        replaceUrl: true,
      });
      return;
    }

    if (returnTo === 'apps') {
      void this.router.navigate(['/reports'], {
        queryParams: returnQuery,
        replaceUrl: true,
      });
      return;
    }

    if (returnTo === 'hours') {
      void this.router.navigate(['/user-report'], {
        queryParams: returnQuery,
        replaceUrl: true,
      });
      return;
    }

    if (returnTo === 'screenshot') {
      // Keep journey on private_proof so the proof screen (with evidence) can render.
      const state = this.activationJourney.getState();
      if (state.currentStep !== 'private_proof') {
        this.activationJourney.goToStep('private_proof');
      }
      void this.router.navigate(['/setup/screenshot'], { replaceUrl: true });
      return;
    }

    const state = this.activationJourney.getState();
    const slug = activationStepToSlug(state.currentStep) || 'ready';
    void this.router.navigate(['/setup', slug], { replaceUrl: true });
  }

  /**
   * Primary CTA — contextual:
   * - entry=ready → start private test (never advance fair-setup from Example)
   * - proof/explore with real proof → fair setup only when proof exists
   * - otherwise return to screenshot proof
   */
  goTryOneEmployee(): void {
    const entry = this.getEntryContext();
    this.markClosed('cta');
    sessionStorage.removeItem(SESSION_RETURN_KEY);
    sessionStorage.removeItem(SESSION_ENTRY_KEY);
    sessionStorage.removeItem(SESSION_RETURN_QUERY_KEY);
    this.activationJourney.trackEvent('example_workday_cta_first_employee', {
      data_source: this.dataSource,
      entry,
    });

    if (entry === 'ready' || entry === 'understand') {
      // Example exit from Understand/Ready: Visible private test — never complete hire from Example.
      this.activationJourney.startPrivateTestFromReady();
      void this.router.navigate(['/setup/install'], { replaceUrl: true });
      this.activationJourney.flushServerSync().subscribe({ error: () => {} });
      return;
    }

    const advanced = this.activationJourney.advanceFromExampleWorkdayToConfirm();
    if (!advanced) {
      // Do not soft-skip unfinished private proof.
      const state = this.activationJourney.getState();
      if (state.currentStep !== 'private_proof') {
        this.activationJourney.goToStep('private_proof');
      }
      void this.router.navigate(['/setup/screenshot'], { replaceUrl: true });
      return;
    }
    void this.router.navigate(['/setup/confirm'], { replaceUrl: true });
    this.activationJourney.flushServerSync().subscribe({ error: () => {} });
  }

  /** Clear ephemeral example session on logout / workspace switch. */
  clearSession(): void {
    sessionStorage.removeItem(SESSION_ACTIVE_KEY);
    sessionStorage.removeItem(SESSION_STEP_KEY);
    sessionStorage.removeItem(SESSION_OPENED_AT_KEY);
    sessionStorage.removeItem(SESSION_RETURN_KEY);
    sessionStorage.removeItem(SESSION_ENTRY_KEY);
    sessionStorage.removeItem(SESSION_RETURN_QUERY_KEY);
  }

  /**
   * Gate for in-page Example preview cards.
   * Trial workspaces only, while setup is still open and no real employee has been hired.
   * Callers must also hide the card when that page already has screenshots or logs.
   */
  shouldOfferExamplePreviews(): boolean {
    if (!this.activationJourney.isEnabled) {
      return false;
    }
    if (!this.activationJourney.isTrialCompany()) {
      return false;
    }
    const state = this.activationJourney.getState();
    if (
      state.journeyStatus === 'completed' ||
      state.journeyStatus === 'skipped' ||
      state.finished ||
      state.skipped
    ) {
      return false;
    }
    const realHire =
      !!state.firstEmployeeId &&
      state.firstEmployeeId !== state.privateTestEmployeeId;
    if (realHire) {
      return false;
    }
    return !!(state.privateEvaluationActive || state.evaluationStatus === 'private_active' || !state.firstEmployeeId);
  }

  isOwnWorkdayReady(): boolean {
    return this.activationJourney.isOwnWorkdayReady();
  }

  /**
   * From Timesheet/Apps Example preview CTA — always Confirm (fairness), never skip.
   */
  goTryOneEmployeeFromExplore(): void {
    this.activationJourney.trackEvent('example_preview_cta_first_employee', {
      data_source: this.dataSource,
    });
    const advanced = this.activationJourney.advanceFromExampleWorkdayToConfirm();
    if (!advanced) {
      const state = this.activationJourney.getState();
      if (state.currentStep !== 'private_proof') {
        this.activationJourney.goToStep('private_proof');
      }
      void this.router.navigate(['/setup/screenshot'], { replaceUrl: true });
      return;
    }
    void this.router.navigate(['/setup/confirm'], { replaceUrl: true });
    this.activationJourney.flushServerSync().subscribe({ error: () => {} });
  }
}
